/* global ResizeObserver, KeyboardEvent */
// ============================================================
// ImageViewer (v8.364.1) - a picture you can actually look at.
//
// Fit to the box by default (a small picture is upscaled), a click
// toggles 1:1 at the cursor, the wheel zooms about the cursor, a drag
// pans with pointer capture, +/-/0/1 keys, and a Fullscreen button on
// the viewer itself. Every number comes from engine/handouts/viewport;
// this component owns the events, the measurements and the transform.
// ============================================================
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type PointerEvent as RPointerEvent,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  type WheelEvent as RWheelEvent,
} from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/HandoutsDrawer.module.css';
import clsx from '../utils/clsx';
import {
  centred,
  clampView,
  fitScale,
  isFit,
  toggleFit,
  zoomAt,
  zoomStep,
  type Size,
  type View,
} from '../engine/handouts';

interface Props {
  src: string;
  alt: string;
  onError?: () => void;
}

const DRAG_SLOP_PX = 4;
const WHEEL_STEP = 1.1;

type Drag = { id: number; startX: number; startY: number; x: number; y: number; moved: boolean };
/** Everything a zoom needs besides the current view. */
type Frame = { img: Size; box: Size; fit: number };

/**
 * The box is measured, not assumed: it changes with the window, the
 * drawer and fullscreen. (Each change refits - a resize is not a zoom.)
 */
function useBoxSize(boxRef: RefObject<HTMLDivElement | null>) {
  const [box, setBox] = useState<Size>({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [boxRef]);
  return box;
}

/** Whether the viewer itself is the fullscreen element, and the button's toggle. */
function useFullscreen(boxRef: RefObject<HTMLDivElement | null>) {
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === boxRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [boxRef]);
  const toggle = () => {
    const el = boxRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (el.requestFullscreen) void el.requestFullscreen();
  };
  return { fullscreen, toggle };
}

/** Step in or out, back to fit, or to true pixels about the box's centre. */
function useZoomCommands(setView: Dispatch<SetStateAction<View>>, { img, box, fit }: Frame) {
  const step = useCallback((dir: 1 | -1) => setView((v) => zoomStep(v, dir, img, box, fit)), [setView, img, box, fit]);
  const toFit = useCallback(() => setView(centred(img, box, fit)), [setView, img, box, fit]);
  const toOne = useCallback(
    () => setView((v) => zoomAt(v, 1 / v.scale, { x: box.width / 2, y: box.height / 2 }, img, box, fit)),
    [setView, img, box, fit],
  );
  return { step, toFit, toOne };
}

type Zoom = ReturnType<typeof useZoomCommands>;

/** +/= and - step, 0 fits, 1 is true pixels; anything else is left alone. */
function useZoomKeys({ step, toFit, toOne }: Zoom) {
  useEffect(() => {
    const keys = new Map<string, () => void>([
      ['+', () => step(1)],
      ['=', () => step(1)],
      ['-', () => step(-1)],
      ['0', toFit],
      ['1', toOne],
    ]);
    const onKey = (e: KeyboardEvent) => {
      const run = keys.get(e.key);
      if (!run) return;
      run();
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, toFit, toOne]);
}

type Cursor = { clientX: number; clientY: number };
type Gesture = { view: View; setView: Dispatch<SetStateAction<View>>; ready: boolean; frame: Frame };

/** The pointer's position inside the box. */
function cursorIn(boxRef: RefObject<HTMLDivElement | null>, e: Cursor) {
  const r = boxRef.current?.getBoundingClientRect();
  return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
}

/** How far a drag has gone, or null while it is still inside the slop and has never left it. */
function dragOffset(d: Drag, e: Cursor): { dx: number; dy: number } | null {
  const dx = e.clientX - d.startX;
  const dy = e.clientY - d.startY;
  if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP_PX) return null;
  return { dx, dy };
}

type DragRef = MutableRefObject<Drag | null>;

/** The drag this pointer owns, if any - a second finger is not ours. */
const dragOf = (drag: DragRef, e: RPointerEvent) => (drag.current?.id === e.pointerId ? drag.current : null);

function startDrag(drag: DragRef, { view, ready }: Gesture) {
  return (e: RPointerEvent) => {
    if (e.button !== 0 || !ready) return;
    drag.current = { id: e.pointerId, startX: e.clientX, startY: e.clientY, x: view.x, y: view.y, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
}

function moveDrag(drag: DragRef, { setView, frame }: Gesture) {
  return (e: RPointerEvent) => {
    const d = dragOf(drag, e);
    const offset = d && dragOffset(d, e);
    if (!d || !offset) return;
    d.moved = true;
    setView((v) =>
      clampView({ scale: v.scale, x: d.x + offset.dx, y: d.y + offset.dy }, frame.img, frame.box, frame.fit),
    );
  };
}

/** A press that never moved is a click, which toggles fit and 1:1 at the cursor. */
function endDrag(drag: DragRef, boxRef: RefObject<HTMLDivElement | null>, { setView, frame }: Gesture) {
  return (e: RPointerEvent) => {
    const d = dragOf(drag, e);
    if (!d) return;
    drag.current = null;
    if (!d.moved) setView((v) => toggleFit(v, cursorIn(boxRef, e), frame.img, frame.box, frame.fit));
  };
}

/** A drag pans with pointer capture once it passes the slop; a click toggles fit. */
function useDragPan(boxRef: RefObject<HTMLDivElement | null>, gesture: Gesture) {
  const drag = useRef<Drag | null>(null);
  return {
    onPointerDown: startDrag(drag, gesture),
    onPointerMove: moveDrag(drag, gesture),
    onPointerUp: endDrag(drag, boxRef, gesture),
    onPointerCancel: () => (drag.current = null),
  };
}

/** The wheel zooms about the cursor. */
function wheelZoom(boxRef: RefObject<HTMLDivElement | null>, { setView, ready, frame }: Gesture) {
  return (e: RWheelEvent) => {
    if (!ready) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    setView((v) => zoomAt(v, factor, cursorIn(boxRef, e), frame.img, frame.box, frame.fit));
  };
}

function ZoomButton({
  onClick,
  title,
  ariaLabel,
  testId,
  children,
}: {
  onClick: () => void;
  title: string;
  ariaLabel?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={common.btnSmall}
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/** The zoom bar: out, the percentage, in, Fit, 1:1, Fullscreen. Presses here never start a drag. */
function ViewerBar({
  pct,
  zoom,
  fullscreen,
  onFullscreen,
}: {
  pct: number;
  zoom: Zoom;
  fullscreen: boolean;
  onFullscreen: () => void;
}) {
  return (
    <div className={styles.viewerBar} onPointerDown={(e) => e.stopPropagation()}>
      <ZoomButton onClick={() => zoom.step(-1)} ariaLabel="Zoom out" title="Zoom out (-)">
        −
      </ZoomButton>
      <span className={styles.viewerPct} data-testid="viewer-zoom">
        {pct}%
      </span>
      <ZoomButton onClick={() => zoom.step(1)} ariaLabel="Zoom in" title="Zoom in (+)">
        +
      </ZoomButton>
      <ZoomButton onClick={zoom.toFit} title="Fit the window (0)" testId="viewer-fit">
        Fit
      </ZoomButton>
      <ZoomButton onClick={zoom.toOne} title="True pixels (1)" testId="viewer-one">
        1:1
      </ZoomButton>
      <ZoomButton onClick={onFullscreen} title="Fullscreen" testId="viewer-fullscreen">
        {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      </ZoomButton>
    </div>
  );
}

export default function ImageViewer({ src, alt, onError }: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [img, setImg] = useState<Size>({ width: 0, height: 0 });
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const box = useBoxSize(boxRef);
  const fit = fitScale(img, box);
  const ready = img.width > 0 && box.width > 0;
  const frame = { img, box, fit };

  useEffect(() => {
    if (ready) setView(centred(img, box, fitScale(img, box)));
  }, [img, box, ready]);
  const { fullscreen, toggle } = useFullscreen(boxRef);
  const zoom = useZoomCommands(setView, frame);
  useZoomKeys(zoom);
  const gesture = { view, setView, ready, frame };
  const pan = useDragPan(boxRef, gesture);

  return (
    <div
      ref={boxRef}
      className={clsx(styles.viewer, fullscreen && styles.viewerFullscreen)}
      onWheel={wheelZoom(boxRef, gesture)}
      {...pan}
      data-testid="image-viewer"
      data-scale={view.scale.toFixed(3)}
      data-fit={ready && isFit(view, fit) ? 'true' : 'false'}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className={styles.viewerImage}
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          visibility: ready ? 'visible' : 'hidden',
        }}
        onLoad={(e) => setImg({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
        onError={onError}
        data-testid="handout-image"
      />
      <ViewerBar pct={Math.round(view.scale * 100)} zoom={zoom} fullscreen={fullscreen} onFullscreen={toggle} />
    </div>
  );
}
